/** The Uploads screen's own vocabulary: what a move is called, and what it does.
 *
 * Which moves a row may make comes from the service — every row arrives with
 * `allowed_next`, and the buttons are drawn from that — so nothing here
 * decides whether an upload may be rejected. What the service does not send
 * is the word on the button and the sentence under the note field, and it
 * should not: those are the portal's to phrase. Keeping them in one file,
 * with a test, means the Uploads table and anything else that offers the
 * same move call it the same thing.
 *
 * Rejecting is the one move the service refuses without a note, because the
 * contributor reads it. That rule is the service's; `transitionNeedsReason`
 * only lets the screen ask before sending rather than after a `400`.
 */

import { count, plural } from '@/lib/format';
import type { UploadStatus } from '@/lib/types';

/** In the order the filter shows them: what is waiting, what is stuck, what
 *  was refused, what landed. */
export const UPLOAD_STATUSES: readonly UploadStatus[] = ['pending', 'held', 'rejected', 'merged'];

export function isUploadStatus(value: string): value is UploadStatus {
  return (UPLOAD_STATUSES as readonly string[]).includes(value);
}

/** The word on the button. */
export function transitionLabel(from: UploadStatus, to: UploadStatus): string {
  switch (to) {
    case 'rejected':
      return 'Reject';
    case 'held':
      return 'Hold';
    case 'pending':
      return from === 'held' ? 'Re-queue' : 'Put back in the queue';
    case 'merged':
      // Never offered: only the job may say a merge happened.
      return 'Merge';
  }
}

/** The service refuses this move without a note. */
export function transitionNeedsReason(to: UploadStatus): boolean {
  return to === 'rejected';
}

/** Reads in the error hue: the move that throws the contributor's work away. */
export function transitionIsDestructive(to: UploadStatus): boolean {
  return to === 'rejected';
}

/** What happens next, under the note field. */
export function transitionConsequence(to: UploadStatus): string {
  switch (to) {
    case 'rejected':
      return 'The contributor sees this note beside the upload. The document stays in the bucket; the job will not read it.';
    case 'held':
      return 'The job skips it until somebody re-queues it. The contributor sees it as held.';
    case 'pending':
      return 'The job reads it on its next run.';
    case 'merged':
      return '';
  }
}

/** Why the table is empty, which depends on what was asked for. */
export function uploadsEmptyTitle(status: UploadStatus | null, narrowed: boolean): string {
  if (status === null) return narrowed ? 'nothing matches these filters' : 'no uploads yet';
  switch (status) {
    case 'pending':
      return narrowed ? 'nothing pending here' : 'nothing is waiting for the job';
    case 'held':
      return narrowed ? 'nothing held here' : 'nothing is held';
    case 'rejected':
      return narrowed ? 'nothing rejected here' : 'nothing has been rejected';
    case 'merged':
      return narrowed ? 'nothing merged here' : 'nothing has merged yet';
  }
}

/** `7 824 edges · 3 runs · 4 crossings` — how big a survey is, in its own units. */
export function sizeSummary(upload: { edges: number; runs: number; crossings: number }): string {
  return `${count(upload.edges)} edges · ${plural(upload.runs, 'run')} · ${plural(upload.crossings, 'crossing')}`;
}
